import {
  Add,
  CheckCircle,
  FilterList,
  Refresh,
  Schedule,
  Support,
} from "@mui/icons-material";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  IconButton,
  Pagination,
  Paper,
  Radio,
  RadioGroup,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHasPermission } from "../hooks/useHasPermission";
import { PERMISSIONS } from "../utils/permissions";
import { useDispatch, useSelector } from "react-redux";
import ParticipantsAvatars from "../components/ParticipantsAvatars";
import {
  getClassificationBadgeSx,
  getClassificationValue,
} from "../utils/complaintClassification";
import {
  fetchClosedTickets,
  fetchOpenTickets,
  fetchTicketsStatistics,
} from "../store/slices/ticketsSlice";
import {
  getAvatarColor,
  getAvatarImageSrc,
  getAvatarInitials,
  hasProfilePicture,
} from "../utils/avatarUtils";
import { getCommunicationMethodIcon } from "../utils/communicationMethodIcons";
import { formatDate } from "../utils/dateFormatter";

const COMMUNICATION_QUICK_FILTER = {
  ALL: "all",
  EXCLUDE_DELIVERY: "exclude_delivery",
  DELIVERY_ONLY: "delivery_only",
};

const HomePage = ({ onNavigate }) => {
  const dispatch = useDispatch();
  const { t, i18n } = useTranslation();
  const {
    openTickets,
    closedTickets,
    statistics,
    pagination,
    isLoading,
    error,
  } = useSelector((state) => state.tickets);
  const { user } = useSelector((state) => state.auth);
  const canCreateTicket = useHasPermission(PERMISSIONS.CREATE_TICKETS);

  // Pagination state
  const [openTicketsPage, setOpenTicketsPage] = useState(1);
  const [closedTicketsPage, setClosedTicketsPage] = useState(1);
  const [openCommunicationFilter, setOpenCommunicationFilter] = useState(
    COMMUNICATION_QUICK_FILTER.ALL
  );
  const [closedCommunicationFilter, setClosedCommunicationFilter] = useState(
    COMMUNICATION_QUICK_FILTER.ALL
  );
  const [filterDialogTarget, setFilterDialogTarget] = useState(null);
  const itemsPerPage = 20;

  // Check if current language is RTL
  const isRTL = i18n.language === "ar";

  useEffect(() => {
    dispatch(fetchTicketsStatistics());
  }, [dispatch]);

  useEffect(() => {
    dispatch(
      fetchOpenTickets({
        page: openTicketsPage,
        pageSize: itemsPerPage,
        communicationFilter: openCommunicationFilter,
      })
    );
  }, [dispatch, openTicketsPage, itemsPerPage, openCommunicationFilter]);

  useEffect(() => {
    dispatch(
      fetchClosedTickets({
        page: closedTicketsPage,
        pageSize: itemsPerPage,
        communicationFilter: closedCommunicationFilter,
      })
    );
  }, [dispatch, closedTicketsPage, itemsPerPage, closedCommunicationFilter]);

  const handleRefresh = () => {
    dispatch(fetchTicketsStatistics());
    dispatch(
      fetchOpenTickets({
        page: openTicketsPage,
        pageSize: itemsPerPage,
        communicationFilter: openCommunicationFilter,
      })
    );
    dispatch(
      fetchClosedTickets({
        page: closedTicketsPage,
        pageSize: itemsPerPage,
        communicationFilter: closedCommunicationFilter,
      })
    );
  };

  const handleQuickFilterChange = (value) => {
    if (filterDialogTarget === "open") {
      setOpenCommunicationFilter(value);
      setOpenTicketsPage(1);
    } else if (filterDialogTarget === "closed") {
      setClosedCommunicationFilter(value);
      setClosedTicketsPage(1);
    }
    setFilterDialogTarget(null);
  };

  const activeQuickFilter =
    filterDialogTarget === "open"
      ? openCommunicationFilter
      : filterDialogTarget === "closed"
        ? closedCommunicationFilter
        : COMMUNICATION_QUICK_FILTER.ALL;

  // Pagination calculations
  const getPaginatedTickets = (tickets, currentPage, itemsPerPage) => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return tickets.slice(startIndex, endIndex);
  };

  const getTotalPages = (totalItems, itemsPerPage) => {
    return Math.ceil(totalItems / itemsPerPage);
  };

  const handleOpenTicketsPageChange = (event, page) => {
    setOpenTicketsPage(page);
  };

  const handleClosedTicketsPageChange = (event, page) => {
    setClosedTicketsPage(page);
  };

  // Get paginated data from Redux store
  const paginatedOpenTickets = openTickets;
  const paginatedClosedTickets = closedTickets;
  const totalOpenPages = pagination.openTickets.totalPages;
  const totalClosedPages = pagination.closedTickets.totalPages;

  const handleViewTicket = (ticketId) => {
    if (onNavigate) {
      onNavigate(`/tickets/${ticketId}`);
    }
  };

  const handleEditTicket = (ticketId) => {
    if (onNavigate) {
      onNavigate(`/tickets/${ticketId}/edit`);
    }
  };

  const handleCreateTicket = () => {
    if (onNavigate) {
      onNavigate("/create-ticket");
    }
  };

  const handleSearch = () => {
    if (onNavigate) {
      onNavigate("/search");
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "open":
        return "warning";
      case "in_progress":
        return "info";
      case "closed":
        return "success";
      case "pending":
        return "default";
      default:
        return "default";
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case "open":
        return t("common.open");
      case "in_progress":
        return t("common.inProgress");
      case "closed":
        return t("common.closed");
      case "pending":
        return t("common.pending");
      default:
        return status;
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case "urgent":
      case "high":
        return "error";
      case "medium":
        return "warning";
      case "low":
        return "success";
      default:
        return "default";
    }
  };

  const getPriorityText = (priority) => {
    switch (priority) {
      case "urgent":
        return t("search.priority.urgent");
      case "high":
        return t("search.priority.high");
      case "medium":
        return t("search.priority.medium");
      case "low":
        return t("search.priority.low");
      default:
        return t("search.priority.medium");
    }
  };

  const getLocalizedName = (item) => {
    if (!item) return "";
    return isRTL
      ? item.name_ar || item.name_en || ""
      : item.name_en || item.name_ar || "";
  };

  const renderCommunicationMethodIcon = (ticket, iconSx = {}) => {
    const iconCode = ticket.communication_method?.icon_code;
    if (!iconCode) return null;
    const IconComponent = getCommunicationMethodIcon(iconCode);
    return (
      <IconComponent
        sx={{
          fontSize: 18,
          color: "inherit",
          mt: 0.25,
          display: "block",
          ...iconSx,
        }}
      />
    );
  };

  const renderTicketNumberBadge = (ticket) => {
    const classification = getClassificationValue(ticket);

    return (
      <Box
        sx={(theme) => ({
          display: "inline-flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minWidth: 52,
          minHeight: 48,
          px: 0.75,
          py: 0.5,
          borderRadius: 1.5,
          flexShrink: 0,
          ...getClassificationBadgeSx(classification, theme),
        })}
      >
        <Typography
          variant="caption"
          sx={{ fontWeight: 700, lineHeight: 1.2, fontSize: "0.72rem" }}
        >
          #{ticket.ticket_id || ticket.id}
        </Typography>
        {renderCommunicationMethodIcon(ticket, { fontSize: 14, mt: 0.25 })}
      </Box>
    );
  };

  const renderBranchTags = (ticket) => {
    const branches = ticket.branches || [];
    if (branches.length === 0) {
      return (
        <Typography variant="body2" color="text.secondary">
          -
        </Typography>
      );
    }

    const firstBranchName = getLocalizedName(branches[0]);
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexWrap: "wrap" }}>
        <Chip label={firstBranchName} size="small" />
        {branches.length > 1 && (
          <Chip label="+" size="small" variant="outlined" />
        )}
      </Box>
    );
  };

  const renderTableSectionHeader = (title, tableTarget, filterValue) => (
    <Box
      sx={{
        position: "relative",
        p: 2,
        borderBottom: 1,
        borderColor: "divider",
      }}
    >
      <Typography
        variant="h6"
        sx={{
          fontWeight: "bold",
          width: "100%",
          textAlign: "start",
        }}
        style={{ paddingLeft: 48 }}
      >
        {title}
      </Typography>
      <Box
        style={{
          position: "absolute",
          left: 8,
          top: "50%",
          transform: "translateY(-50%)",
        }}
      >
        <Tooltip title={t("home.quickFilter.title")}>
          <IconButton
            aria-label={t("home.quickFilter.title")}
            onClick={() => setFilterDialogTarget(tableTarget)}
            color={
              filterValue !== COMMUNICATION_QUICK_FILTER.ALL
                ? "primary"
                : "default"
            }
          >
            <FilterList />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );

  const renderTicketTableRow = (ticket) => (
    <TableRow
      key={ticket.ticket_id || ticket.id}
      hover
      onClick={() => handleViewTicket(ticket.ticket_id || ticket.id)}
      sx={{ cursor: "pointer" }}
    >
      <TableCell sx={{ width: "8.33%" }}>
        {renderTicketNumberBadge(ticket)}
      </TableCell>
      <TableCell sx={{ width: "16.66%" }}>
        {formatDate(ticket.created_date)}
      </TableCell>
      <TableCell sx={{ width: "25%" }}>
        {ticket.title || ticket.subject || t("common.unknown")}
      </TableCell>
      <TableCell sx={{ width: "8.33%", textAlign: "center" }}>
        <ParticipantsAvatars
          participants={getTicketParticipants(ticket)}
          maxDisplay={3}
          size="small"
        />
      </TableCell>
      <TableCell sx={{ width: "25%" }}>
        {(() => {
          const latestComment = getLatestComment(ticket);
          return latestComment ? (
            <Typography
              variant="body2"
              sx={{
                maxWidth: 400,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {latestComment.message}
            </Typography>
          ) : (
            <Typography variant="body2" color="text.secondary">
              -
            </Typography>
          );
        })()}
      </TableCell>
      <TableCell sx={{ width: "8.33%" }}>
        {(() => {
          const latestComment = getLatestComment(ticket);
          return latestComment ? (
            <Avatar
              src={getAvatarImageSrc(latestComment.author)}
              sx={{
                width: 32,
                height: 32,
                fontSize: "0.875rem",
                bgcolor: hasProfilePicture(latestComment.author)
                  ? "transparent"
                  : getAvatarColor(latestComment.author),
              }}
            >
              {hasProfilePicture(latestComment.author)
                ? undefined
                : getInitials(latestComment.author)}
            </Avatar>
          ) : (
            <Typography variant="body2" color="text.secondary">
              -
            </Typography>
          );
        })()}
      </TableCell>
      <TableCell sx={{ width: "10%" }}>{renderBranchTags(ticket)}</TableCell>
      <TableCell sx={{ width: "8.33%" }}>
        <Box
          sx={{
            display: "flex",
            gap: 1,
            alignItems: "center",
            justifyContent: isRTL ? "flex-end" : "flex-start",
          }}
        >
          <Chip
            label={getStatusText(ticket.status)}
            color={getStatusColor(ticket.status)}
            size="small"
          />
          <Chip
            label={getPriorityText(ticket.priority || "medium")}
            color={getPriorityColor(ticket.priority || "medium")}
            size="small"
            variant="outlined"
          />
        </Box>
      </TableCell>
    </TableRow>
  );

  const tableHeadRow = (
    <TableRow>
      <TableCell sx={{ width: "8.33%" }}>
        {t("home.tables.ticketNumber")}
      </TableCell>
      <TableCell sx={{ width: "16.66%" }}>
        {t("home.tables.createdAt")}
      </TableCell>
      <TableCell sx={{ width: "25%" }}>{t("home.tables.subject")}</TableCell>
      <TableCell sx={{ width: "8.33%", textAlign: "center" }}>
        {t("home.tables.participants")}
      </TableCell>
      <TableCell sx={{ width: "25%" }}>
        {t("home.tables.latestComment")}
      </TableCell>
      <TableCell sx={{ width: "8.33%" }}>
        {t("home.tables.commentAuthor")}
      </TableCell>
      <TableCell sx={{ width: "10%" }}>{t("home.tables.branch")}</TableCell>
      <TableCell sx={{ width: "8.33%" }}>{t("home.tables.status")}</TableCell>
    </TableRow>
  );

  const getTicketParticipants = (ticket) => {
    const participants = [];

    // Add ticket creator - try multiple possible fields
    const creator =
      ticket.add_by_user_id ||
      ticket.user ||
      ticket.add_by_user ||
      ticket.created_by;
    if (creator) {
      const participant = {
        user_id: creator.id || creator.user_id,
        username: creator.username || creator.user_name || "مستخدم",
        first_name: creator.first_name,
        last_name: creator.last_name,
        email: creator.email || creator.user_email,
        profile_picture: creator.profile_picture,
        avatar: creator.avatar || creator.profile_image || creator.image,
      };

      participants.push(participant);
    }

    // Add reply participants
    if (ticket.replies && Array.isArray(ticket.replies)) {
      ticket.replies.forEach((reply) => {
        if (reply.user?.username || reply.user_name || reply.username) {
          const replyParticipant = {
            user_id: reply.user?.id || reply.user_id,
            username:
              reply.user?.username ||
              reply.user_name ||
              reply.username ||
              "مستخدم",
            first_name: reply.user?.first_name,
            last_name: reply.user?.last_name,
            email: reply.user?.email || reply.email || reply.user_email,
            profile_picture: reply.user?.profile_picture,
            avatar: reply.avatar || reply.profile_image || reply.image,
          };
          participants.push(replyParticipant);
        }
      });
    }

    return participants;
  };

  const getLatestComment = (ticket) => {
    if (
      !ticket.replies ||
      !Array.isArray(ticket.replies) ||
      ticket.replies.length === 0
    ) {
      return null;
    }

    // Sort replies by date and get the latest one
    const sortedReplies = ticket.replies
      .filter((reply) => reply.message || reply.comment || reply.text)
      .sort((a, b) => {
        const dateA = new Date(
          a.reply_date || a.created_at || a.created_date || a.date
        );
        const dateB = new Date(
          b.reply_date || b.created_at || b.created_date || b.date
        );
        return dateB - dateA;
      });

    if (sortedReplies.length === 0) {
      return null;
    }

    const latestReply = sortedReplies[0];
    const author = {
      user_id: latestReply.user?.id || latestReply.user_id,
      username:
        latestReply.user?.username ||
        latestReply.user_name ||
        latestReply.username ||
        "مستخدم",
      first_name: latestReply.user?.first_name,
      last_name: latestReply.user?.last_name,
      email:
        latestReply.user?.email || latestReply.email || latestReply.user_email,
      profile_picture: latestReply.user?.profile_picture,
      avatar:
        latestReply.avatar || latestReply.profile_image || latestReply.image,
    };

    return {
      message: latestReply.message || latestReply.comment || latestReply.text,
      author: author,
      date:
        latestReply.reply_date ||
        latestReply.created_at ||
        latestReply.created_date ||
        latestReply.date,
    };
  };

  // Use unified avatar utilities
  const getInitials = (author) => {
    return getAvatarInitials(author);
  };

  // Use unified avatar color function
  const getAvatarColorLocal = (author) => {
    return getAvatarColor(author);
  };

  const TicketCard = ({ ticket, isRTL }) => {
    const latestComment = getLatestComment(ticket);

    return (
      <Card
        sx={{
          mb: 2,
          cursor: "pointer",
          "&:hover": { boxShadow: 3 },
        }}
        onClick={() => handleViewTicket(ticket.ticket_id || ticket.id)}
      >
        <CardContent sx={{ p: 2 }}>
          {/* Header */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              mb: 2,
            }}
          >
            <Box>
              {renderTicketNumberBadge(ticket)}
            </Box>
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", justifyContent: "flex-end" }}>
              <Chip
                label={getStatusText(ticket.status)}
                color={getStatusColor(ticket.status)}
                size="small"
              />
              <Chip
                label={getPriorityText(ticket.priority || "medium")}
                color={getPriorityColor(ticket.priority || "medium")}
                size="small"
                variant="outlined"
              />
            </Box>
          </Box>

          {/* Subject */}
          <Box sx={{ mb: 2 }}>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontSize: "0.75rem" }}
            >
              {t("home.tables.branch")}
            </Typography>
            <Box sx={{ mt: 0.5 }}>{renderBranchTags(ticket)}</Box>
          </Box>

          {/* Subject */}
          <Box sx={{ mb: 2 }}>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontSize: "0.75rem" }}
            >
              {t("home.tables.subject")}
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: "medium", mt: 0.5 }}>
              {ticket.title || ticket.subject || t("common.unknown")}
            </Typography>
          </Box>

          {/* Created Date */}
          <Box sx={{ mb: 2 }}>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontSize: "0.75rem" }}
            >
              {t("home.tables.createdAt")}
            </Typography>
            <Typography variant="body2" sx={{ mt: 0.5 }}>
              {formatDate(ticket.created_date)}
            </Typography>
          </Box>

          {/* Participants */}
          <Box sx={{ mb: 2 }}>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontSize: "0.75rem" }}
            >
              {t("home.tables.participants")}
            </Typography>
            <Box sx={{ mt: 0.5, display: "flex", justifyContent: "center" }}>
              <ParticipantsAvatars
                participants={getTicketParticipants(ticket)}
                maxDisplay={3}
                size="small"
              />
            </Box>
          </Box>

          {/* Latest Comment */}
          {latestComment && (
            <Box sx={{ mb: 2 }}>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontSize: "0.75rem" }}
              >
                {t("home.tables.latestComment")}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  mt: 0.5,
                  display: "-webkit-box",
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                }}
              >
                {latestComment.message}
              </Typography>
            </Box>
          )}

          {/* Comment Author */}
          {latestComment && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontSize: "0.75rem" }}
              >
                {t("home.tables.commentAuthor")}:
              </Typography>
              <Avatar
                src={getAvatarImageSrc(latestComment.author)}
                sx={{
                  width: 24,
                  height: 24,
                  fontSize: "0.75rem",
                  bgcolor: hasProfilePicture(latestComment.author)
                    ? undefined
                    : getAvatarColor(latestComment.author),
                }}
              >
                {hasProfilePicture(latestComment.author)
                  ? undefined
                  : getInitials(latestComment.author)}
              </Avatar>
              <Typography variant="body2" sx={{ fontSize: "0.875rem" }}>
                {latestComment.author.user_name ||
                  latestComment.author.username ||
                  "Unknown"}
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>
    );
  };

  // Check if there are no tickets at all
  const hasNoTickets = !isLoading && statistics.total_tickets === 0 && !error;

  return (
    <Container dir={isRTL ? "rtl" : "ltr"} maxWidth="xl" sx={{ mt: 1, mb: 2 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
            flexDirection: { xs: "column", sm: "row" },
            gap: 2,
          }}
        >
          <Typography
            variant="h4"
            component="h1"
            sx={{
              fontWeight: "bold",
              textAlign: { xs: "center", sm: isRTL ? "right" : "left" },
            }}
          >
            {t("home.title")}
          </Typography>
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            <Button
              variant="outlined"
              startIcon={<Refresh />}
              onClick={handleRefresh}
              disabled={isLoading}
            >
              {t("common.refresh")}
            </Button>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={handleCreateTicket}
              sx={{ display: canCreateTicket ? undefined : "none" }}
            >
              {t("common.createTicket")}
            </Button>
            <Button variant="outlined" onClick={handleSearch}>
              {t("common.search")}
            </Button>
          </Box>
        </Box>

        {/* Stats Cards - Compact Design */}
        <Box sx={{ flexGrow: 1 }}>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Card sx={{ height: "100%", width: "100%" }}>
                <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <Support color="warning" sx={{ fontSize: 24, mx: 1.5 }} />
                    <Box sx={{ px: 1 }}>
                      <Typography
                        variant="body1"
                        color="text.secondary"
                        sx={{ fontSize: "0.9rem", mb: 0.5 }}
                      >
                        {t("home.stats.openTickets")}
                      </Typography>
                      <Typography
                        variant="h5"
                        component="div"
                        sx={{ fontWeight: "bold" }}
                      >
                        {statistics.open_tickets}
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Card sx={{ height: "100%" }}>
                <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <CheckCircle
                      color="success"
                      sx={{ fontSize: 24, mx: 1.5 }}
                    />
                    <Box sx={{ px: 1 }}>
                      <Typography
                        variant="body1"
                        color="text.secondary"
                        sx={{ fontSize: "0.9rem", mb: 0.5 }}
                      >
                        {t("home.stats.closedTickets")}
                      </Typography>
                      <Typography
                        variant="h5"
                        component="div"
                        sx={{ fontWeight: "bold" }}
                      >
                        {statistics.closed_tickets}
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Card sx={{ height: "100%" }}>
                <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <Schedule color="info" sx={{ fontSize: 24, mx: 1.5 }} />
                    <Box sx={{ px: 1 }}>
                      <Typography
                        variant="body1"
                        color="text.secondary"
                        sx={{ fontSize: "0.9rem", mb: 0.5 }}
                      >
                        {t("home.stats.highPriority")}
                      </Typography>
                      <Typography
                        variant="h5"
                        component="div"
                        sx={{ fontWeight: "bold" }}
                      >
                        {statistics.high_priority_tickets}
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Card sx={{ height: "100%" }}>
                <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <Support color="primary" sx={{ fontSize: 24, mx: 1.5 }} />
                    <Box sx={{ px: 1 }}>
                      <Typography
                        variant="body1"
                        color="text.secondary"
                        sx={{ fontSize: "0.9rem", mb: 0.5 }}
                      >
                        {t("home.stats.totalTickets")}
                      </Typography>
                      <Typography
                        variant="h5"
                        component="div"
                        sx={{ fontWeight: "bold" }}
                      >
                        {statistics.total_tickets}
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Box>
      </Box>

      {/* Error Messages */}
      {error && (
        <Box sx={{ mb: 3 }}>
          <Alert severity="error" sx={{ mb: 2 }}>
            {t("home.errors.fetchError")}: {error}
          </Alert>
          <Alert severity="info" sx={{ mb: 2 }}>
            {t("home.errors.serverConnection")}
          </Alert>
          <Button
            variant="contained"
            startIcon={<Refresh />}
            onClick={handleRefresh}
            sx={{ mb: 2 }}
          >
            {t("common.refresh")}
          </Button>
        </Box>
      )}

      {/* Show message if no tickets at all */}
      {hasNoTickets && (
        <Paper sx={{ p: 4, mb: 4, textAlign: "center" }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            {t("home.errors.noTicketsFound")}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {t("home.errors.serverConnection")}
          </Typography>
          {canCreateTicket && (
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={handleCreateTicket}
              sx={{ mx: 1 }}
            >
              {t("common.createTicket")}
            </Button>
          )}
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={handleRefresh}
            sx={{ mx: 1 }}
          >
            {t("common.refresh")}
          </Button>
        </Paper>
      )}

      {/* Open Tickets Table */}
      {!hasNoTickets && (
        <Paper sx={{ mb: 4 }}>
          {renderTableSectionHeader(
            `${t("home.tables.openTickets")} (${pagination.openTickets.totalCount})`,
            "open",
            openCommunicationFilter
          )}

          {/* Desktop Table */}
          <Box sx={{ display: { xs: "none", md: "block" } }}>
            <TableContainer>
              <Table>
                <TableHead>{tableHeadRow}</TableHead>
                <TableBody>
                  {isLoading ? (
                    <TableRow>
                      <TableCell colSpan={8} align="center">
                        <CircularProgress />
                      </TableCell>
                    </TableRow>
                  ) : paginatedOpenTickets.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} align="center">
                        <Typography variant="body1" sx={{ mb: 1 }}>
                          {t("home.errors.noTicketsFound")}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    paginatedOpenTickets.map((ticket) =>
                      renderTicketTableRow(ticket)
                    )
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>

          {/* Pagination for Desktop */}
          {totalOpenPages > 0 && paginatedOpenTickets.length > 0 && (
            <Box
              sx={{
                display: { xs: "none", md: "flex" },
                justifyContent: "space-between",
                alignItems: "center",
                p: 2,
              }}
            >
              <Typography variant="body2" color="text.secondary">
                {t("home.pagination.showing")}{" "}
                {(openTicketsPage - 1) * itemsPerPage + 1}{" "}
                {t("home.pagination.to")}{" "}
                {Math.min(
                  openTicketsPage * itemsPerPage,
                  pagination.openTickets.totalCount
                )}{" "}
                {t("home.pagination.of")} {pagination.openTickets.totalCount}{" "}
                {t("home.pagination.results")}
              </Typography>
              <Pagination
                count={totalOpenPages}
                page={openTicketsPage}
                onChange={handleOpenTicketsPageChange}
                color="primary"
                size="large"
                showFirstButton
                showLastButton
              />
            </Box>
          )}

          {/* Mobile Cards */}
          <Box sx={{ display: { xs: "block", md: "none" }, p: 2 }}>
            {isLoading ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                <CircularProgress />
              </Box>
            ) : paginatedOpenTickets.length === 0 ? (
              <Box sx={{ textAlign: "center", py: 4 }}>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  {t("home.errors.noTicketsFound")}
                </Typography>
              </Box>
            ) : (
              paginatedOpenTickets.map((ticket) => (
                <TicketCard
                  key={ticket.ticket_id || ticket.id}
                  ticket={ticket}
                  isRTL={isRTL}
                />
              ))
            )}
          </Box>

          {/* Pagination for Mobile */}
          {totalOpenPages > 0 && paginatedOpenTickets.length > 0 && (
            <Box sx={{ display: { xs: "block", md: "none" }, p: 2 }}>
              <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ textAlign: "center" }}
                >
                  {t("home.pagination.showing")}{" "}
                  {(openTicketsPage - 1) * itemsPerPage + 1}{" "}
                  {t("home.pagination.to")}{" "}
                  {Math.min(
                    openTicketsPage * itemsPerPage,
                    pagination.openTickets.totalCount
                  )}{" "}
                  {t("home.pagination.of")} {pagination.openTickets.totalCount}{" "}
                  {t("home.pagination.results")}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", justifyContent: "center" }}>
                <Pagination
                  count={totalOpenPages}
                  page={openTicketsPage}
                  onChange={handleOpenTicketsPageChange}
                  color="primary"
                  size="medium"
                  showFirstButton
                  showLastButton
                />
              </Box>
            </Box>
          )}
        </Paper>
      )}

      {/* Closed Tickets Table */}
      {!hasNoTickets && (
        <Paper>
          {renderTableSectionHeader(
            `${t("home.tables.closedTickets")} (${pagination.closedTickets.totalCount})`,
            "closed",
            closedCommunicationFilter
          )}

          {/* Desktop Table */}
          <Box sx={{ display: { xs: "none", md: "block" } }}>
            <TableContainer>
              <Table>
                <TableHead>{tableHeadRow}</TableHead>
                <TableBody>
                  {isLoading ? (
                    <TableRow>
                      <TableCell colSpan={8} align="center">
                        <CircularProgress />
                      </TableCell>
                    </TableRow>
                  ) : paginatedClosedTickets.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} align="center">
                        <Typography variant="body1" sx={{ mb: 1 }}>
                          {t("home.errors.noTicketsFound")}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    paginatedClosedTickets.map((ticket) =>
                      renderTicketTableRow(ticket)
                    )
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>

          {/* Pagination for Desktop */}
          {totalClosedPages > 0 && paginatedClosedTickets.length > 0 && (
            <Box
              sx={{
                display: { xs: "none", md: "flex" },
                justifyContent: "space-between",
                alignItems: "center",
                p: 2,
              }}
            >
              <Typography variant="body2" color="text.secondary">
                {t("home.pagination.showing")}{" "}
                {(closedTicketsPage - 1) * itemsPerPage + 1}{" "}
                {t("home.pagination.to")}{" "}
                {Math.min(
                  closedTicketsPage * itemsPerPage,
                  pagination.closedTickets.totalCount
                )}{" "}
                {t("home.pagination.of")} {pagination.closedTickets.totalCount}{" "}
                {t("home.pagination.results")}
              </Typography>
              <Pagination
                count={totalClosedPages}
                page={closedTicketsPage}
                onChange={handleClosedTicketsPageChange}
                color="primary"
                size="large"
                showFirstButton
                showLastButton
              />
            </Box>
          )}

          {/* Mobile Cards */}
          <Box sx={{ display: { xs: "block", md: "none" }, p: 2 }}>
            {isLoading ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                <CircularProgress />
              </Box>
            ) : paginatedClosedTickets.length === 0 ? (
              <Box sx={{ textAlign: "center", py: 4 }}>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  {t("home.errors.noTicketsFound")}
                </Typography>
              </Box>
            ) : (
              paginatedClosedTickets.map((ticket) => (
                <TicketCard
                  key={ticket.ticket_id || ticket.id}
                  ticket={ticket}
                  isRTL={isRTL}
                />
              ))
            )}
          </Box>

          {/* Pagination for Mobile */}
          {totalClosedPages > 0 && paginatedClosedTickets.length > 0 && (
            <Box sx={{ display: { xs: "block", md: "none" }, p: 2 }}>
              <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ textAlign: "center" }}
                >
                  {t("home.pagination.showing")}{" "}
                  {(closedTicketsPage - 1) * itemsPerPage + 1}{" "}
                  {t("home.pagination.to")}{" "}
                  {Math.min(
                    closedTicketsPage * itemsPerPage,
                    pagination.closedTickets.totalCount
                  )}{" "}
                  {t("home.pagination.of")}{" "}
                  {pagination.closedTickets.totalCount}{" "}
                  {t("home.pagination.results")}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", justifyContent: "center" }}>
                <Pagination
                  count={totalClosedPages}
                  page={closedTicketsPage}
                  onChange={handleClosedTicketsPageChange}
                  color="primary"
                  size="medium"
                  showFirstButton
                  showLastButton
                />
              </Box>
            </Box>
          )}
        </Paper>
      )}

      <Dialog
        open={Boolean(filterDialogTarget)}
        onClose={() => setFilterDialogTarget(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>{t("home.quickFilter.title")}</DialogTitle>
        <DialogContent>
          <RadioGroup
            value={activeQuickFilter}
            onChange={(event) => handleQuickFilterChange(event.target.value)}
          >
            <FormControlLabel
              value={COMMUNICATION_QUICK_FILTER.ALL}
              control={<Radio />}
              label={t("home.quickFilter.all")}
            />
            <FormControlLabel
              value={COMMUNICATION_QUICK_FILTER.EXCLUDE_DELIVERY}
              control={<Radio />}
              label={t("home.quickFilter.excludeDelivery")}
            />
            <FormControlLabel
              value={COMMUNICATION_QUICK_FILTER.DELIVERY_ONLY}
              control={<Radio />}
              label={t("home.quickFilter.deliveryOnly")}
            />
          </RadioGroup>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFilterDialogTarget(null)}>
            {t("common.close")}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default HomePage;
